import { API_BASE_URL } from "../config/apiConfig";

const BASE_URL = "/api/v1/public/events";

/**
 * Public (unauthenticated) events API.
 *
 * Uses plain fetch rather than APIService because these endpoints are reached
 * by visitors with no session — APIService's auth/CSRF handling and global
 * error popups are wrong for a marketing surface. Mirrors DemoUserService.
 */
const apiUrl = (path) => `${API_BASE_URL}${BASE_URL}${path}`;

const headers = () => ({
  "Content-Type": "application/json",
  "x-api-key": process.env.REACT_APP_API_KEY,
});

const request = async (path, options = {}) => {
  try {
    const response = await fetch(apiUrl(path), {
      credentials: "include",
      headers: headers(),
      ...options,
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) {
      return {
        success: false,
        message: data.message || "Something went wrong. Please try again.",
        status: response.status,
      };
    }
    return data;
  } catch (error) {
    console.error("EventService request failed:", error);
    return {
      success: false,
      message: "We couldn't reach the server. Please check your connection.",
    };
  }
};

const icsUrl = (slug) => apiUrl(`/${encodeURIComponent(slug)}/ics`);

const EventService = {
  /** @param {"upcoming"|"past"|"all"} scope */
  listEvents: (scope = "upcoming") =>
    request(`?scope=${encodeURIComponent(scope)}`),

  getEvent: (slug) => request(`/${encodeURIComponent(slug)}`),

  /** Direct link — kept as the fallback when the blob fetch below fails. */
  icsUrl,

  icsFileName: (slug) => `kinshield-${slug}.ics`,

  /**
   * Returns an object URL for the .ics body, or "" if it could not be read.
   *
   * Linking straight at the API origin makes mobile Safari open a download
   * interstitial and then strand the visitor on a blank api.kinprotect.com
   * tab once the calendar sheet closes. A same-page blob avoids that.
   */
  fetchIcsBlobUrl: async (slug) => {
    try {
      const response = await fetch(icsUrl(slug), {
        credentials: "include",
        headers: headers(),
      });
      if (!response.ok) return "";
      const body = await response.text();
      return URL.createObjectURL(
        new Blob([body], { type: "text/calendar;charset=utf-8" }),
      );
    } catch (error) {
      console.error("EventService ICS fetch failed:", error);
      return "";
    }
  },

  register: (slug, payload) =>
    request(`/${encodeURIComponent(slug)}/register`, {
      method: "POST",
      body: JSON.stringify(payload),
    }),

  getRegistration: (token) =>
    request(`/registration/${encodeURIComponent(token)}`),

  /** @param {"cancel"|"update"|"resend"|"transfer"|"request-code"|"verify"|"claim-seat"} action */
  registrationAction: (token, action, payload = {}) =>
    request(`/registration/${encodeURIComponent(token)}`, {
      method: "POST",
      body: JSON.stringify({ action, ...payload }),
    }),

  submitHostRequest: (payload) =>
    request("/host-request", {
      method: "POST",
      body: JSON.stringify(payload),
    }),
};

export default EventService;
